import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/cn';
import { formatMoney } from '../../lib/format';
import type { CashflowReport } from './api';

/**
 * **A YEAR NOBODY HAS PLANNED IS NOT A FORECAST OF DISASTER.**
 *
 * Standing expense schedules run forever; invoices and projected lines do not.
 * So clicking the year-forward arrow renders every dollar of cost and no
 * revenue at all: `/budget-forecast?year=2027` printed Ending cash
 * **(1,023,480)** and Profit **(1,264,340)** in red across the whole grid, and
 * `/dashboard?year=2028` matched it with "CASH ON DEC 31, 2028 ($1,023,480) ·
 * The low point of the year". None of that had been forecast by anybody — it is
 * an artefact of the asymmetry between a schedule and a plan.
 *
 * The honest answer is to say so ONCE, at the top, on **every** screen that
 * renders the year: the dashboard had this guard and the grid it links to did
 * not, which is the worse half of the pair — the grid is where the reader goes
 * to find out how bad it is.
 *
 * ⚠ `projectedPotential` is deliberately NOT part of the test. Potential is
 * below the line and counted in nothing, so 2027's $46,850 of unticked deals
 * was suppressing this notice on a year with literally no plan.
 */
export function isPlanlessYear(report: CashflowReport): boolean {
  return (
    !report.plan.saved && report.totals.income === 0 && report.totals.projectedAdditional === 0
  );
}

export function PlanlessYearNotice({
  report,
  planHref,
  className,
  /** `bare` drops the card chrome — the Report tab renders it as a strip
   *  inside the grid card, the dashboard as a standalone block. */
  bare = false,
}: {
  report: CashflowReport;
  /** Omit on the Report tab: the plan bar is the next element down, and a link
   *  to something already on screen is a step, not an affordance. */
  planHref?: string;
  className?: string;
  bare?: boolean;
}) {
  if (!isPlanlessYear(report)) return null;
  const currency = report.currency;
  const money = (value: number) => formatMoney(value, { currency, whole: true });
  return (
    <div
      className={cn(
        'flex items-start gap-2 text-body-sm leading-relaxed text-grey-600',
        bare
          ? 'border-b border-grey-200 bg-grey-50 px-4 py-2.5'
          : 'rounded-lg border border-grey-200 bg-grey-50 px-3 py-2.5',
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-grey-400" aria-hidden />
      <span>
        <strong className="font-bold text-secondary">No {report.year} plan yet.</strong> Nothing has
        been invoiced or projected for {report.year}, but your standing cash-out schedule (
        <span className="num">{money(report.totals.expenses)}</span>) and recurring templates (
        <span className="num">{money(report.totals.revenue)}</span>) carry forward on their own.
        Everything below is therefore a <strong className="font-semibold">costs-only</strong>{' '}
        projection, not a forecast — set a goal and a cash plan
        {planHref ? (
          <>
            {' '}
            on{' '}
            <Link to={planHref} className="font-semibold text-accent hover:underline">
              Budget &amp; Forecast
            </Link>
          </>
        ) : (
          ' on the bar below'
        )}
        , or add projected revenue, and it becomes one.
      </span>
    </div>
  );
}
